import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CalendarDays, ChevronLeft, ChevronRight, Clock3, Settings2, Sparkles } from 'lucide-react'
import {
  useGetCalendarEventsQuery,
  useGetMilestoneEventsQuery,
  useGetCollaborationCalendarEventsQuery,
} from '@/redux/collaborationsApi'
import { Button } from '@/components/ui/button'
import { BUTTON_STYLES } from '@/lib/buttonStyles'

// Event type color coding
const EVENT_COLORS: Record<
  string,
  {
    bg: string
    border: string
    dot: string
    label: string
    icon: string
    gradient: string
  }
> = {
  nil: {
    bg: 'rgba(245,158,11,0.12)',
    border: 'rgba(245,158,11,0.3)',
    dot: '#f59e0b',
    label: 'NIL Deadline',
    icon: '⚡',
    gradient: 'from-[#fe9a00] to-[#f54900]',
  },
  post: {
    bg: 'rgba(6,182,212,0.12)',
    border: 'rgba(6,182,212,0.3)',
    dot: '#06b6d4',
    label: 'Post Reminder',
    icon: '📱',
    gradient: 'from-[#00b8db] to-[#155dfc]',
  },
  approval: {
    bg: 'rgba(192,253,114,0.1)',
    border: 'rgba(192,253,114,0.25)',
    dot: '#C0FD72',
    label: 'Creative Approval',
    icon: '✓',
    gradient: 'from-[#9ae600] to-[#05df72]',
  },
  collab_event: {
    bg: 'rgba(139,92,246,0.12)',
    border: 'rgba(139,92,246,0.30)',
    dot: '#8b5cf6',
    label: 'Collab Event',
    icon: '📅',
    gradient: 'from-[#8b5cf6] to-[#6d28d9]',
  },
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay()
}

function formatDate(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function formatEventTimeLabel(event: any) {
  if (event.allDay) {
    return 'All day'
  }

  const start = event.start ? new Date(event.start) : null

  if (!start || Number.isNaN(start.getTime())) {
    return ''
  }

  return start.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatEventDuration(event: any) {
  if (event.allDay || !event.start || !event.end) {
    return ''
  }

  const start = new Date(event.start)
  const end = new Date(event.end)

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return ''
  }

  const diffMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000))

  if (diffMinutes < 60) {
    return `${diffMinutes}m`
  }

  const hours = Math.floor(diffMinutes / 60)
  const minutes = diffMinutes % 60

  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`
}

function getEventDateKey(event: any) {
  if (event.date) {
    return event.date
  }

  if (event.start) {
    return String(event.start).slice(0, 10)
  }

  return ''
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#6a7282]">
      {children}
    </p>
  )
}

function DarkCard({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={`rounded-2xl border border-[#1e2939] bg-[#131824] shadow-[0_20px_25px_rgba(0,0,0,0.1),0_8px_10px_rgba(0,0,0,0.1)] ${className}`}
    >
      {children}
    </div>
  )
}

export default function CalendarView() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const today = new Date()

  // Derive year/month from URL params, defaulting to today
  const year = Number(searchParams.get('year')) || today.getFullYear()
  const month = Number(searchParams.get('month') ?? today.getMonth() + 1) - 1

  const [selectedDate, setSelectedDate] = useState(
    formatDate(today.getFullYear(), today.getMonth(), today.getDate()),
  )

  const daysInMonth = getDaysInMonth(year, month)
  const firstDay = getFirstDayOfMonth(year, month)

  const monthParams = useMemo(
    () => ({
      start: new Date(Date.UTC(year, month, 1)).toISOString(),
      end: new Date(Date.UTC(year, month + 1, 1)).toISOString(),
    }),
    [year, month],
  )

  const {
    data: calendarData,
    isLoading: calendarLoading,
    isError: calendarError,
  } = useGetCalendarEventsQuery(monthParams)

  const { data: milestoneData, isLoading: milestoneLoading } =
    useGetMilestoneEventsQuery(monthParams)

  const { data: collabEventData } = useGetCollaborationCalendarEventsQuery(monthParams)

  const loading = calendarLoading || milestoneLoading
  const loadError = calendarError ? 'Failed to load Google Calendar events' : null
  const googleCalendarConnected = calendarData?.connected !== false

  function setViewMonth(newYear: number, newMonth: number) {
    setSearchParams({ year: String(newYear), month: String(newMonth + 1) })
  }

  function prevMonth() {
    if (month === 0) setViewMonth(year - 1, 11)
    else setViewMonth(year, month - 1)
  }

  function nextMonth() {
    if (month === 11) setViewMonth(year + 1, 0)
    else setViewMonth(year, month + 1)
  }

  const combinedEvents = useMemo(() => {
    const googleEvents = calendarData?.events ?? []
    const milestoneEvents = milestoneData?.events ?? []
    const mappedGoogleEvents = googleEvents.map((event) => ({
      ...event,
      date: getEventDateKey(event),
      time: formatEventTimeLabel(event),
      duration: formatEventDuration(event),
      campaign: null,
    }))

    const collabEvents = collabEventData?.events ?? []
    return [...milestoneEvents, ...mappedGoogleEvents, ...collabEvents]
  }, [calendarData, milestoneData, collabEventData])

  const eventsForSelected = combinedEvents.filter((e) => e.date === selectedDate)
  const upcomingDeadlines = combinedEvents
    .filter((e) => e.type === 'nil' || e.type === 'approval')
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 3)

  const isToday = (d: number) =>
    today.getFullYear() === year && today.getMonth() === month && today.getDate() === d

  const isSelected = (d: number) => selectedDate === formatDate(year, month, d)

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-6 pb-10 pt-1">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1.5">
          <h1 className="text-[30px] font-bold leading-9 text-white">Calendar</h1>
          <p className="text-sm text-[#99a1af] sm:text-base">Your schedule + NIL deliverables</p>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button
            onClick={() => navigate('/calendar-setup')}
            className={`h-10 rounded-[14px] px-4 text-sm font-medium ${BUTTON_STYLES.secondary}`}
          >
            <Settings2 className="h-4 w-4" />
            Manage
          </Button>
        </div>
      </div>

      <div className="rounded-[14px] border border-[rgba(43,127,255,0.2)] bg-linear-to-r from-[rgba(43,127,255,0.1)] to-[rgba(0,184,219,0.1)] px-4 py-4">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#8ec5ff]" />
          <p className="text-sm leading-5 text-[#d1d5dc]">
            Your athlete calendar blends school, team, and NIL deliverables into one clean view so
            you can spot conflicts early and stay on schedule.
          </p>
        </div>
      </div>

      {loadError ? (
        <div className="rounded-[14px] border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          {loadError}
        </div>
      ) : null}

      <section className="space-y-4">
        <SectionLabel>Calendar overview</SectionLabel>
        <DarkCard className="p-5">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#6a7282]">
                Month view
              </p>
              <div className="mt-1 flex items-center gap-2">
                <p className="text-xl font-semibold text-white">
                  {MONTHS[month]} {year}
                </p>
                {loading ? (
                  <svg
                    className="h-4 w-4 animate-spin text-[#6a7282]"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                ) : null}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={prevMonth}
                className="flex h-10 w-10 items-center justify-center rounded-[14px] border border-[#2a3441] bg-[#1a2030] text-[#99a1af] transition-colors hover:text-white"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={nextMonth}
                className="flex h-10 w-10 items-center justify-center rounded-[14px] border border-[#2a3441] bg-[#1a2030] text-[#99a1af] transition-colors hover:text-white"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap gap-2.5">
            {Object.entries(EVENT_COLORS).map(([key, val]) => (
              <div
                key={key}
                className="inline-flex items-center gap-2 rounded-full border border-[rgba(54,65,83,0.55)] bg-[#1a2030] px-3 py-1.5"
              >
                <div className="h-2 w-2 rounded-full" style={{ background: val.dot }} />
                <span className="text-[11px] text-[#99a1af]">{val.label}</span>
              </div>
            ))}
          </div>

          <div className="overflow-hidden rounded-[18px] border border-[#1e2939] bg-[#101522]">
            <div className="grid grid-cols-7 border-b border-[#1e2939]">
              {DAYS.map((d) => (
                <div
                  key={d}
                  className="py-3 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6a7282]"
                >
                  {d}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-px bg-[#1e2939]">
              {Array.from({ length: firstDay }).map((_, i) => (
                <div key={`empty-${i}`} className="min-h-[74px] bg-[#101522]" />
              ))}

              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1
                const dateStr = formatDate(year, month, day)
                const dayEvents = combinedEvents.filter((e) => e.date === dateStr)
                const hasEvent = dayEvents.length > 0
                const selected = isSelected(day)
                const todayCell = isToday(day)

                return (
                  <button
                    key={day}
                    onClick={() => setSelectedDate(dateStr)}
                    className={`group relative min-h-[74px] bg-[#101522] p-2 text-left transition-colors hover:bg-[#141b2b] ${selected ? 'ring-1 ring-inset ring-[#2b7fff]' : ''}`}
                  >
                    <div
                      className={`mb-4 flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors ${selected ? 'bg-[#C0FD72] text-black' : todayCell ? 'border border-[#C0FD72] text-[#C0FD72]' : 'text-[#d1d5dc] group-hover:text-white'}`}
                    >
                      {day}
                    </div>

                    {hasEvent ? (
                      <div className="flex flex-wrap gap-1">
                        {dayEvents.slice(0, 3).map((e) => (
                          <span
                            key={`${e.type}-${e.id}`}
                            className="h-1.5 w-1.5 rounded-full"
                            style={{ background: EVENT_COLORS[e.type]?.dot }}
                          />
                        ))}
                      </div>
                    ) : null}
                  </button>
                )
              })}
            </div>
          </div>
        </DarkCard>
      </section>

      <section className="space-y-4">
        <SectionLabel>Selected day</SectionLabel>
        <DarkCard className="p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#6a7282]">
                Active date
              </p>
              <p className="mt-1 text-lg font-semibold text-white">
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>

            <div className="flex items-center gap-2 rounded-full border border-[rgba(54,65,83,0.55)] bg-[#1a2030] px-3 py-1.5 text-[11px] text-[#99a1af]">
              <Clock3 className="h-3.5 w-3.5" />
              {eventsForSelected.length} event{eventsForSelected.length === 1 ? '' : 's'}
            </div>
          </div>

          {eventsForSelected.length === 0 ? (
            <div className="rounded-[16px] border border-[rgba(54,65,83,0.45)] bg-[#1a2030] px-4 py-8 text-center">
              <p className="text-sm text-[#99a1af]">No events on this day</p>
            </div>
          ) : (
            <div className="space-y-3">
              {eventsForSelected.map((event) => {
                const color = EVENT_COLORS[event.type]
                const isLinked = Boolean(event.collaborationId)
                return (
                  <div
                    key={`${event.type}-${event.id}`}
                    onClick={
                      isLinked
                        ? () => navigate(`/collaborations/${event.collaborationId}`)
                        : undefined
                    }
                    className={`flex items-start gap-3 rounded-[16px] border border-[rgba(54,65,83,0.45)] bg-[#1a2030] px-4 py-4 ${isLinked ? 'cursor-pointer transition-colors hover:border-[rgba(54,65,83,0.8)] hover:bg-[#1e2640]' : ''}`}
                  >
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] bg-linear-to-br text-sm font-semibold text-white ${color.gradient}`}
                    >
                      {color.icon}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold leading-tight text-white">
                            {event.title}
                          </p>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[#99a1af]">
                            <span>{event.time}</span>
                            {event.duration ? (
                              <>
                                <span className="text-[#4b5565]">•</span>
                                <span>{event.duration}</span>
                              </>
                            ) : null}
                            {event.calendarSummary ? (
                              <>
                                <span className="text-[#4b5565]">•</span>
                                <span>{event.calendarSummary}</span>
                              </>
                            ) : null}
                            {event.campaign ? (
                              <>
                                <span className="text-[#4b5565]">•</span>
                                <span>{event.campaign}</span>
                              </>
                            ) : null}
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          <div
                            className="rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]"
                            style={{ background: color.bg, color: color.dot }}
                          >
                            {color.label}
                          </div>
                          {isLinked ? <ChevronRight className="h-4 w-4 text-[#6a7282]" /> : null}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </DarkCard>
      </section>

      <section className="space-y-4">
        <SectionLabel>Upcoming NIL deadlines</SectionLabel>
        <DarkCard className="p-5">
          <div className="mb-4 flex items-center gap-2 text-sm text-[#99a1af]">
            <CalendarDays className="h-4 w-4 text-[#8ec5ff]" />
            Upcoming milestones that need your attention
          </div>

          <div className="space-y-3">
            {upcomingDeadlines.map((event) => {
              const color = EVENT_COLORS[event.type]
              const d = new Date(event.date + 'T12:00:00')
              const isLinked = Boolean(event.collaborationId)

              return (
                <div
                  key={event.id}
                  onClick={
                    isLinked
                      ? () => navigate(`/collaborations/${event.collaborationId}`)
                      : undefined
                  }
                  className={`flex items-center gap-3 rounded-[16px] border border-[rgba(54,65,83,0.45)] bg-[#1a2030] px-4 py-4 ${isLinked ? 'cursor-pointer transition-colors hover:border-[rgba(54,65,83,0.8)] hover:bg-[#1e2640]' : ''}`}
                >
                  <div
                    className={`flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-[14px] bg-linear-to-br text-white ${color.gradient}`}
                  >
                    <span className="text-[10px] font-semibold uppercase leading-none text-white/80">
                      {d.toLocaleDateString('en-US', { month: 'short' })}
                    </span>
                    <span className="mt-1 text-base font-bold leading-none">{d.getDate()}</span>
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-white">{event.title}</p>
                    <p className="mt-0.5 text-xs text-[#99a1af]">
                      {event.campaign || color.label}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <div
                      className="rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]"
                      style={{ background: color.bg, color: color.dot }}
                    >
                      {color.label}
                    </div>
                    {isLinked ? <ChevronRight className="h-4 w-4 text-[#6a7282]" /> : null}
                  </div>
                </div>
              )
            })}
          </div>
        </DarkCard>
      </section>
    </div>
  )
}
